import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  SidebarDropdownMenu,
  SidebarDropdownMenuContent,
  SidebarDropdownMenuTrigger,
  SidebarMenuButton,
} from "@design-system/components";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { getActiveWorkspaceStorageOptions } from "@spiriidk/connect-configs";
import { useLocalStorageSync } from "@spiriidk/utils";
import { ChevronDown } from "lucide-react";
import React, { useId, useMemo } from "react";
import { navigateToUrl } from "single-spa";

import type { WorkspaceOption } from "../../utils";

const HOME_PATH = "/dashboard";
const HOME_PATHS = new Set(["/", HOME_PATH]);

function isOnHomePage() {
  const pathname = window.location.pathname.replace(/\/+$/, "") || "/";
  return HOME_PATHS.has(pathname);
}

function goHome() {
  if (isOnHomePage()) {
    window.location.reload();
    return;
  }
  navigateToUrl(HOME_PATH);
}

interface WorkspaceSelectorProps {
  options: WorkspaceOption[];
  userId: string;
}

type WorkspaceAvatarProps = Pick<WorkspaceOption, "imageUrl" | "label">;

function WorkspaceAvatar({ imageUrl, label }: WorkspaceAvatarProps) {
  if (!imageUrl) return null;

  return (
    <Avatar variant="company">
      <AvatarImage src={imageUrl} alt="" />
      <AvatarFallback>{label.charAt(0)}</AvatarFallback>
    </Avatar>
  );
}

function WorkspaceSelector({ options, userId }: WorkspaceSelectorProps) {
  const { _ } = useLingui();
  const labelId = useId();
  const storageOptions = useMemo(
    () => getActiveWorkspaceStorageOptions(userId),
    [userId]
  );
  const [storedWorkspaceId, setStoredWorkspaceId] =
    useLocalStorageSync(storageOptions);
  const selectedWorkspace =
    options.find((option) => option.value === storedWorkspaceId) ?? options[0];

  if (!selectedWorkspace) return null;

  const selectedWorkspaceId = selectedWorkspace.value;

  function selectWorkspace(workspaceId: string) {
    if (workspaceId === selectedWorkspaceId) return;
    setStoredWorkspaceId(workspaceId);
    goHome();
  }

  return (
    <div className="-ml-2 min-w-0 flex-1">
      <SidebarDropdownMenu>
        <SidebarDropdownMenuTrigger>
          <SidebarMenuButton
            className="h-10 pl-2"
            data-testid="workspace-selector"
            data-track-name="workspace-selector"
            data-track-label="Workspace selector"
            data-track-context="main-navigation"
          >
            <WorkspaceAvatar
              imageUrl={selectedWorkspace.imageUrl}
              label={selectedWorkspace.label}
            />
            <span className="min-w-0 truncate">
              <span className="sr-only">{_(msg`Workspace:`)} </span>
              {selectedWorkspace.label}
            </span>
            <ChevronDown className="ml-auto" />
          </SidebarMenuButton>
        </SidebarDropdownMenuTrigger>
        <SidebarDropdownMenuContent
          align="start"
          className="max-h-[250px] min-w-56"
        >
          <DropdownMenuGroup aria-labelledby={labelId}>
            <DropdownMenuLabel id={labelId} className="truncate">
              {_(msg`Change workspace`)}
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={selectedWorkspaceId}
              onValueChange={selectWorkspace}
            >
              {options.map((option) => (
                <DropdownMenuRadioItem
                  key={option.value}
                  value={option.value}
                  indicatorSide="right"
                  data-track-name="workspace-option"
                  data-track-context="main-navigation"
                >
                  <WorkspaceAvatar
                    imageUrl={option.imageUrl}
                    label={option.label}
                  />
                  <span className="min-w-0 truncate">{option.label}</span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuGroup>
        </SidebarDropdownMenuContent>
      </SidebarDropdownMenu>
    </div>
  );
}

export { WorkspaceSelector };
export type { WorkspaceSelectorProps };
